import { useEffect, useState } from 'react';
import { safeFetch } from '@/api/safeFetch';

interface PopupComentariosProps {
  perfil_id: number;
  onClose: () => void;
}

interface Comentario {
  comentario_id: number;
  nombre_usuario: string;
  comentario: string;
  puntuacion: number;
  estado?: 'publicado' | 'revisando';
}

const PopupComentarios: React.FC<PopupComentariosProps> = ({ perfil_id, onClose }) => {
  const [comentarios, setComentarios] = useState<Comentario[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchComentarios = async () => {
    setLoading(true);
    try {
      const res = await safeFetch(`/api/comentarios?perfil_id=${perfil_id}`);
      if (!res.ok) throw new Error('Error al cargar comentarios');
      const data = await res.json();
      setComentarios(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (perfil_id) fetchComentarios();
  }, [perfil_id]);

  const handleSetReview = async (
    id: number,
    estado: 'revisando' | 'publicado' = 'revisando'
  ) => {
    try {
      const res = await safeFetch(`/api/comentarios/${id}/estado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado }),
      });
      if (!res.ok) throw new Error('Error al cambiar estado');
      fetchComentarios();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await safeFetch(`/api/comentarios/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Error al borrar comentario');
      fetchComentarios();
    } catch (err) {
      console.error(err);
    }
  };

  const publicados = comentarios.filter(c => c.estado === 'publicado');
  const revisando = comentarios.filter(c => c.estado === 'revisando');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 text-zinc-700">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <button className="text-right w-full mb-2 text-red-500" onClick={onClose}>
          Cerrar ✖
        </button>

        <h2 className="text-xl font-bold mb-4">Comentarios Públicos</h2>

        {loading ? (
          <p>Cargando...</p>
        ) : publicados.length ? (
          <ul className="space-y-2">
            {publicados.map(c => (
              <li
                key={c.comentario_id}
                className="border p-2 rounded flex justify-between items-start"
              >
                <div>
                  <p>
                    <strong>{c.nombre_usuario}</strong> ({c.puntuacion}⭐)
                  </p>
                  <p>{c.comentario}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    className="bg-yellow-400 text-black px-2 py-1 rounded text-sm hover:bg-yellow-500"
                    onClick={() => handleSetReview(c.comentario_id)}
                  >
                    Marcar revisión
                  </button>
                  <button
                    className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                    onClick={() => handleDelete(c.comentario_id)}
                  >
                    Borrar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : revisando.length ? (
          <p className="text-center italic text-gray-600">
            🕒 Estamos revisando su comentario
          </p>
        ) : (
          <p>No hay comentarios públicos.</p>
        )}

        <h2 className="text-xl font-bold mt-6 mb-4">Comentarios en Revisión</h2>

        {loading ? (
          <p>Cargando...</p>
        ) : revisando.length ? (
          <ul className="space-y-2">
            {revisando.map(c => (
              <li
                key={c.comentario_id}
                className="border p-2 rounded flex justify-between items-start bg-yellow-50"
              >
                <div>
                  <p>
                    <strong>{c.nombre_usuario}</strong> ({c.puntuacion}⭐) 🕒 En
                    revisión
                  </p>
                  <p>{c.comentario}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    className="bg-green-500 text-white px-2 py-1 rounded text-sm hover:bg-green-600"
                    onClick={() =>
                      handleSetReview(c.comentario_id, 'publicado')
                    }
                  >
                    Volver a publicar
                  </button>
                  <button
                    className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                    onClick={() => handleDelete(c.comentario_id)}
                  >
                    Borrar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No hay comentarios en revisión.</p>
        )}
      </div>
    </div>
  );
};

export default PopupComentarios;
